import { watch } from "vue";
import { RouteRecordRaw } from "vue-router";
import {
  NavMenuItem,
  registerNavMenuItem,
  BreadcrumbParam,
  addBreadCrumbs,
} from "@/core/layout";
import { addRoutes } from "@/core/router";
import { isUserLoggedIn } from "@/core/auth";
import { isDataStoreEnabled } from "@/datastore/services/datastore.service";

// Route guards on top of the in-page canDataStore checks: the router refuses to
// navigate without DATA_STORE.PAGE.VIEW, and the gateway refuses the calls (design
// §8.2 — the UI layers are UX, the gateway is the security).
const datastoreListRoute: RouteRecordRaw = {
  path: "/datastore",
  name: "datastoreTables",
  component: () => import("@/datastore/pages/DataStoreTables.vue"),
  meta: {
    authorize: ["DATA_STORE.PAGE.VIEW"],
  },
};

const datastoreEditRoute: RouteRecordRaw = {
  path: "/datastore/edit/:id",
  name: "editDataStoreTable",
  component: () => import("@/datastore/pages/EditDataStoreTable.vue"),
  meta: {
    authorize: ["DATA_STORE.PAGE.VIEW"],
  },
};

addRoutes([datastoreListRoute, datastoreEditRoute]);

const datastoreMenuItem: NavMenuItem = {
  iconName: "table",
  label: "Data Store",
  name: "datastoreTables",
  targetRoute: {
    path: "/datastore",
    name: "datastoreTables",
    component: () => import("@/datastore/pages/DataStoreTables.vue"),
    meta: {
      authorize: ["DATA_STORE.PAGE.VIEW"],
    },
  },
  authority: "DATA_STORE.PAGE.VIEW",
  rank: 54,
  isParent: false,
  hasParent: false,
};

// DATASTORE_ENABLED comes from the unauthenticated config/env, so it is resolved
// once here at module load. The login transition below then reads a settled
// promise instead of racing a config read against the sidebar's mount —
// navMenuItems is a plain array (core/layout), so a push that lands after the
// sidebar has rendered is a lost update.
const dataStoreEnabled = isDataStoreEnabled().catch((error) => {
  console.warn("Failed to check datastore visibility:", error);
  return false;
});

let menuItemRegistered = false;

// Registered on the login transition rather than once at module evaluation:
// feature packages are imported a single time per page load (core/vue/startup.ts
// → loadFeaturePackages), and a cold open lands on the login page with no
// session, so a one-shot isUserLoggedIn check there would leave the menu missing
// for the whole session that follows. registerNavMenuItem throws on a duplicate
// rank, hence the guard — the watcher fires again on every re-login.
const createDataStoreMenuItem = async () => {
  if (menuItemRegistered) {
    return;
  }
  if (!(await dataStoreEnabled)) {
    return;
  }
  if (menuItemRegistered) {
    return;
  }
  menuItemRegistered = true;
  registerNavMenuItem(datastoreMenuItem);
};

watch(
  isUserLoggedIn,
  (loggedIn) => {
    if (loggedIn) {
      createDataStoreMenuItem();
    }
  },
  { immediate: true }
);

const datastoreBreadcrumbs: BreadcrumbParam[] = [
  {
    routeName: "datastoreTables",
    items: [
      {
        labelKey: "Auth.Home",
        route: "/dashboard",
        disabled: false,
        isTranslated: true,
        uniqueLabel: "",
      },
      {
        labelKey: "DataStore.Tables",
        route: "/datastore",
        disabled: true,
        isTranslated: true,
        uniqueLabel: "",
      },
    ],
  },
  {
    routeName: "editDataStoreTable",
    items: [
      {
        labelKey: "Auth.Home",
        route: "/dashboard",
        disabled: false,
        isTranslated: true,
        uniqueLabel: "",
      },
      {
        labelKey: "DataStore.Tables",
        route: "/datastore",
        disabled: false,
        isTranslated: true,
        uniqueLabel: "",
      },
      {
        labelKey: "DataStore.Table",
        route: "",
        disabled: true,
        isTranslated: true,
        uniqueLabel: "",
      },
    ],
  },
];

addBreadCrumbs(datastoreBreadcrumbs);
